import { getFallbackCampaignLanding } from "../data/campaignLandingFallbacks";

const MANIFEST_URL = `${process.env.PUBLIC_URL || ""}/data/romi-landings.json`;
const BLOCK_TYPES = new Set(["marquee", "html", "image", "carousel", "button", "benefits", "form"]);
const EFFECTS = new Set(["none", "pulse", "bounce", "shine"]);
const FONT_STYLES = new Set(["normal", "italic"]);
const TEXT_ALIGNS = new Set(["left", "center", "right"]);
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

const isRecord = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const cleanText = (value, limit = 240) => typeof value === "string" ? value.trim().slice(0, limit) : "";
const cleanNumber = (value, min, max, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};
const cleanColor = (value, fallback) => HEX_COLOR.test(String(value || "")) ? String(value) : fallback;
const cleanAssetUrl = (value) => {
  const url = cleanText(value, 900);
  if (!url) return "";
  if (url.startsWith("/") && !url.startsWith("//") && !url.includes("..")) return url;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : "";
  } catch {
    return "";
  }
};
const cleanTextList = (value, limit = 12) => Array.isArray(value)
  ? value.map((item) => cleanText(item, 140)).filter(Boolean).slice(0, limit)
  : [];

// Los bloques HTML provienen del editor local de ROMI. Aun así retiramos los
// vectores ejecutables más evidentes antes de insertarlos en la tienda pública.
const cleanHtml = (value) => cleanText(value, 30000)
  .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "")
  .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe\s*>/gi, "")
  .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
  .replace(/(?:javascript|data)\s*:/gi, "");

function normalizeFormText(value) {
  if (!isRecord(value)) return undefined;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => /^[a-z][a-zA-Z]{1,40}$/.test(key))
    .map(([key, text]) => [key, cleanText(text, 260)])
    .filter(([, text]) => Boolean(text)));
}

function normalizeBlock(value, index) {
  if (!isRecord(value) || !BLOCK_TYPES.has(value.type)) return null;
  const block = {
    id: cleanText(value.id, 80) || `block-${index + 1}`,
    type: value.type,
    label: cleanText(value.label, 120) || `Bloque ${index + 1}`,
  };
  const url = cleanAssetUrl(value.url);
  const images = Array.isArray(value.images) ? value.images.map(cleanAssetUrl).filter(Boolean).slice(0, 20) : [];
  if (value.type === "html") block.html = cleanHtml(value.html);
  if (url) block.url = url;
  if (images.length) block.images = images;
  if (typeof value.text === "string") block.text = cleanText(value.text, 500);
  const items = cleanTextList(value.items, 20);
  if (items.length) block.items = items;
  if (HEX_COLOR.test(String(value.background || ""))) block.background = value.background;
  if (HEX_COLOR.test(String(value.color || ""))) block.color = value.color;
  if (typeof value.buttonText === "string") block.buttonText = cleanText(value.buttonText, 120);
  if (HEX_COLOR.test(String(value.buttonBackground || ""))) block.buttonBackground = value.buttonBackground;
  if (HEX_COLOR.test(String(value.buttonColor || ""))) block.buttonColor = value.buttonColor;
  if (EFFECTS.has(value.buttonEffect)) block.buttonEffect = value.buttonEffect;
  block.buttonRadius = cleanNumber(value.buttonRadius, 0, 80, 40);
  ["fontFamily", "headingFontFamily", "buttonFontFamily"].forEach((key) => {
    const family = cleanText(value[key], 80).replace(/[^a-zA-Z0-9 ,'-]/g, "");
    if (family) block[key] = family;
  });
  ["fontSize", "headingFontSize", "buttonFontSize", "formLabelFontSize", "formInputFontSize"].forEach((key) => {
    if (value[key] !== undefined) block[key] = cleanNumber(value[key], 8, 96, 16);
  });
  ["fontWeight", "headingFontWeight", "buttonFontWeight"].forEach((key) => {
    if (value[key] !== undefined) block[key] = cleanNumber(value[key], 100, 900, 400);
  });
  ["fontStyle", "headingFontStyle", "buttonFontStyle"].forEach((key) => {
    if (FONT_STYLES.has(value[key])) block[key] = value[key];
  });
  ["textAlign", "headingAlign"].forEach((key) => {
    if (TEXT_ALIGNS.has(value[key])) block[key] = value[key];
  });
  const formText = normalizeFormText(value.formText);
  if (formText && Object.keys(formText).length) block.formText = formText;
  const formProductImage = cleanAssetUrl(value.formProductImage);
  if (formProductImage) block.formProductImage = formProductImage;
  return block;
}

function normalizeEditor(value) {
  if (!isRecord(value) || !Array.isArray(value.blocks)) return null;
  let formSeen = false;
  const blocks = value.blocks.slice(0, 80).map(normalizeBlock).filter((block) => {
    if (!block) return false;
    if (block.type !== "form") return true;
    if (formSeen) return false;
    formSeen = true;
    return true;
  });
  if (!blocks.length || !blocks.some((block) => block.type === "form")) return null;
  return {
    pageBackground: cleanColor(value.pageBackground, "#f4efe6"),
    textColor: cleanColor(value.textColor, "#171717"),
    buttonBackground: cleanColor(value.buttonBackground, "#f2c94c"),
    buttonColor: cleanColor(value.buttonColor, "#171717"),
    formBackground: cleanColor(value.formBackground, "#ffffff"),
    contentWidth: cleanNumber(value.contentWidth, 320, 1600, 720),
    blocks,
  };
}

function normalizeOffers(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const offers = [];
  value.slice(0, 6).forEach((item) => {
    if (!isRecord(item)) return;
    const qty = Math.trunc(Number(item.qty));
    const price = Number(item.price);
    if (!Number.isInteger(qty) || qty < 1 || qty > 100 || !Number.isFinite(price) || price <= 0 || price > 100000 || seen.has(qty)) return;
    seen.add(qty);
    offers.push({ qty, price: Math.round(price * 100) / 100, featured: item.featured === true });
  });
  if (offers.length && !offers.some((item) => item.featured)) offers[0].featured = true;
  return offers;
}

export function normalizeCampaignLanding(value, expectedSlug = "") {
  if (!isRecord(value)) return null;
  const slug = cleanText(value.slug || expectedSlug, 100).toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || (expectedSlug && slug !== expectedSlug)) return null;
  const productId = cleanText(value.productId, 120);
  const product = cleanText(value.product, 190);
  const images = Array.isArray(value.images) ? value.images.map(cleanAssetUrl).filter(Boolean).slice(0, 20) : [];
  const offers = normalizeOffers(value.offers);
  if (!/^romi-ai-\d+$/.test(productId) || product.length < 3 || !images.length || !offers.length) return null;
  const themeValue = isRecord(value.theme) ? value.theme : {};
  const editor = normalizeEditor(value.editor);
  return {
    slug,
    productId,
    product,
    subtitle: cleanText(value.subtitle, 300) || "Una solución práctica pensada para ti",
    images,
    offers,
    ...(cleanAssetUrl(value.extraImage) ? { extraImage: cleanAssetUrl(value.extraImage) } : {}),
    ...(cleanTextList(value.marquee).length ? { marquee: cleanTextList(value.marquee) } : {}),
    ...(cleanTextList(value.ctas, 20).length ? { ctas: cleanTextList(value.ctas, 20) } : {}),
    theme: {
      page: cleanColor(themeValue.page, "#020712"),
      ink: cleanColor(themeValue.ink, "#13aec3"),
      accent: cleanColor(themeValue.accent, "#00d3fe"),
      accentInk: cleanColor(themeValue.accentInk, "#071018"),
      border: cleanColor(themeValue.border, "#315df0"),
      form: cleanColor(themeValue.form, "#dee3ed"),
      formInk: cleanColor(themeValue.formInk, "#020712"),
    },
    ...(editor ? { editor } : {}),
  };
}

export async function loadCampaignLanding(slug, { signal } = {}) {
  const fallback = getFallbackCampaignLanding(slug);
  try {
    const response = await fetch(MANIFEST_URL, { cache: "no-store", signal });
    if (!response.ok) return fallback;
    const manifest = await response.json();
    if (!isRecord(manifest) || manifest.schemaVersion !== 1 || !Array.isArray(manifest.landings)) return fallback;
    const candidate = manifest.landings.find((item) => item?.slug === slug);
    return normalizeCampaignLanding(candidate, slug) || fallback;
  } catch (error) {
    if (error?.name !== "AbortError") console.warn("No se pudo cargar el manifiesto de landings; se usará el respaldo local.", error);
    return fallback;
  }
}

export { getFallbackCampaignLanding };
